import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { getXpProgress } from "../../helpers/xpLevel"
import { getUserFullName } from "../../helpers/userDisplay"

export const PROFILE_USER_WITH = [
  "currentRoles.roleTypes",
  "currentRoles.categories",
  "addresses",
]

const extractCityList = result => {
  if (Array.isArray(result)) return result
  if (Array.isArray(result?.data)) return result.data
  return []
}

const hydrateAddressesWithCities = (addresses = [], cities = []) => {
  const byId = new Map(
    cities.filter(city => city?.id).map(city => [String(city.id), city]),
  )
  return addresses.map(address => {
    if (address?.city) return address
    const city = byId.get(String(address?.city_id || ""))
    return city ? { ...address, city } : address
  })
}

export const fetchUserProfile = async userId => {
  if (!userId) return null
  try {
    const withQuery = PROFILE_USER_WITH.map(
      value => `with[]=${encodeURIComponent(value)}`,
    ).join("&")
    const [userRes, citiesRes] = await Promise.all([
      get(`${endpoints.getUser(userId)}?${withQuery}`),
      get(endpoints.getCites, { skipErrorToast: true }),
    ])
    if (!userRes?.data?.success) return null
    const user = userRes.data.result
    if (!user) return null
    const cities = extractCityList(citiesRes?.data?.result)
    return {
      ...user,
      addresses: hydrateAddressesWithCities(user.addresses || [], cities),
    }
  } catch {
    return null
  }
}

export const getProfileDisplayName = (user, { fallback = "—" } = {}) =>
  getUserFullName(user, { fallback })

export const getProfileCurrentRoles = user =>
  user?.current_roles || user?.currentRoles || []

const getRoleName = role => {
  if (!role) return ""
  if (typeof role === "string") return role.trim()
  return role.public_name || role.name || ""
}

const getCurrentRoleTypeName = item => {
  const types = item?.role_types || item?.roleTypes || []
  return getRoleName(item?.role) || getRoleName(types[0])
}

export const getProfileRoleLabel = user => {
  const roles = getProfileCurrentRoles(user)
  const ongoing = roles.find(item => item?.is_ongoing)
  return (
    getCurrentRoleTypeName(ongoing) ||
    roles.map(getCurrentRoleTypeName).find(Boolean) ||
    user?.role_types?.[0]?.public_name ||
    user?.role_types?.[0]?.name ||
    user?.role_type?.public_name ||
    user?.role_type?.name ||
    user?.ongoing_roles?.[0]?.name ||
    ""
  )
}

export const getProfileXp = user => getXpProgress(user)

export const getAddressByType = (addresses = [], type) =>
  addresses.find(address => address?.address_type === type) || null

export const formatCityName = address => {
  const city = address?.city
  if (!city) return "--"
  if (typeof city === "string") return city
  const name = city.city || city.name || ""
  const state = city.state || ""
  if (name && state) return `${name}, ${state}`
  return name || "--"
}

const ADDRESS_TYPE_ORDER = ["primary", "secondary", "tertiary"]

const ADDRESS_TYPE_LABELS = {
  primary: "Primary",
  secondary: "Secondary",
  tertiary: "Tertiary",
}

/** PreferredCitySelect options from the signed-in user's profile addresses. */
export const getUserAddressCityOptions = (user = null) => {
  const addresses = Array.isArray(user?.addresses) ? user.addresses : []
  const seenCityIds = new Set()

  return [...addresses]
    .sort((a, b) => {
      const aIndex = ADDRESS_TYPE_ORDER.indexOf(a?.address_type)
      const bIndex = ADDRESS_TYPE_ORDER.indexOf(b?.address_type)
      return (
        (aIndex === -1 ? ADDRESS_TYPE_ORDER.length : aIndex) -
        (bIndex === -1 ? ADDRESS_TYPE_ORDER.length : bIndex)
      )
    })
    .reduce((options, address) => {
      const cityId = address?.city_id || address?.city?.id
      if (!cityId || seenCityIds.has(String(cityId))) return options

      seenCityIds.add(String(cityId))
      const cityName = formatCityName(address)
      const typeLabel = ADDRESS_TYPE_LABELS[address?.address_type]

      options.push({
        value: cityId,
        label:
          cityName !== "--"
            ? typeLabel
              ? `${cityName} (${typeLabel})`
              : cityName
            : typeLabel || "City",
      })
      return options
    }, [])
}
